import React from "react";
//#region types
import { FileEnums, StyleEnums } from "models/enums";
import { FileUploaderProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { Icon, StyledButton } from "components/common";
import {
  ExpandableSection,
  ExpandableSectionSummary,
  FileUploader,
  TextAreaInput,
  VerticalFormField,
  WintForm,
  useWintForm,
} from "components/common/wintForms";
import useCurrentAgentConversation from "./useCurrentAgentConversation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

interface AgentAskFormValues {
  text: string;
  attachments: UploadedFile[];
}

interface AgentAskFormProps {
  onSend: (text: string, files: UploadedFile[]) => void;
}

const QuestionMinRows = 2;
const t = LocalizationHelperInstance.getFormattedMessage;

const AgentAskForm: React.FC<AgentAskFormProps> = ({ onSend }) => {
  const { wintFormProps, connect, values, submit } = useWintForm<AgentAskFormValues>({
    defaultValues: { text: "", attachments: [] },
    handleSubmit: async (formValues) => onSend(formValues.text.trim(), formValues.attachments ?? []),
  });
  const { conversation } = useCurrentAgentConversation();

  // Once each time the form is shown, so a popper left before a word was written can be told from one left mid-question.
  const typed = !!values.text?.trim();
  const typedTracked = React.useRef(false);
  React.useEffect(() => {
    if (!typed || typedTracked.current) return;
    typedTracked.current = true;
    conversation.track("question.typed");
  }, [typed, conversation]);

  return (
    <Grid container direction="column" className={cssExports.agentAskForm}>
      <WintForm {...wintFormProps}>
        <VerticalFormField<TextInputProps>
          Component={TextAreaInput}
          {...connect("text")}
          label={t(TranslationKeys.SupportIssue.AgentAskPlaceholder)}
          minRows={QuestionMinRows}
        />
        <ExpandableSection
          className={cssExports.agentAskAttach}
          onToggle={(open) => conversation.track("attach.toggled", { open })}
          summary={
            <ExpandableSectionSummary
              title={t(TranslationKeys.Common.AddFile)}
              subText={t(TranslationKeys.Common.Optional)}
            />
          }
        >
          <FileUploader
            {...(connect("attachments") as FileUploaderProps)}
            allowedTypes={[FileEnums.ContentTypes.ApplicationPDF, FileEnums.ContentTypes.CSV]}
            allowAnyImageTypes
          />
        </ExpandableSection>
        <Grid container className={cssExports.agentAskFoot}>
          <p className={cssExports.agentAskPromise}>{t(TranslationKeys.SupportIssue.AgentAskPromise)}</p>
          <StyledButton
            size="sm"
            displayType={StyleEnums.DisplayType.Info}
            disabled={!values.text?.trim()}
            onClick={() => submit()}
            endIcon={<Icon icon={IconDefinitionConstants.WChevronSmallRight} />}
            testId="agent-ask-send"
          >
            {t(TranslationKeys.Common.Continue)}
          </StyledButton>
        </Grid>
      </WintForm>
    </Grid>
  );
};

export default AgentAskForm;
